/**
 * Renata — Hub de navegación (/app/renata)
 * Cards por sección. Mismo diseño que el resto de los hubs merchant.
 */
import { createFileRoute } from "@tanstack/react-router";
import { RenataAvatar } from "@/components/renata/RenataAvatar";
import { useEffect, useState } from "react";
import {
  MessageSquare, Lightbulb, Zap, Target, Activity, BookMarked, Sparkles,
  UserCheck, Store, Settings, BarChart2, Map, BookOpen, Flag, Workflow,
  Inbox, Bell, ArrowRight, Clock, RefreshCw, ShoppingCart,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { renataChatSearch } from "@/lib/renata/experience/chat-search";
import { DashboardCard, MERCHANT_HUB_GRID } from "@/components/ds";
import { Link } from "@tanstack/react-router";
import { useRenataFlags } from "@/hooks/use-renata-flags";
import type { RenataFeatureId } from "@/lib/renata/copilot-features";

export const Route = createFileRoute("/app/renata/")({
  component: RenataHubPage,
  head: () => ({ meta: [{ title: "Renata" }] }),
});

interface QuickStats {
  pendingAprobaciones: number;
  planesActivos: number;
  oportunidadesAbiertas: number;
}

interface HubCard {
  to: string;
  label: string;
  description: string;
  icon: LucideIcon;
  search?: Record<string, string>;
  featureFlag?: RenataFeatureId;
}

interface HubSection {
  id: string;
  title: string;
  cards: HubCard[];
}

function buildSections(stats: QuickStats | null): HubSection[] {
  const pending = stats?.pendingAprobaciones ?? 0;
  const planes = stats?.planesActivos ?? 0;
  const opps = stats?.oportunidadesAbiertas ?? 0;

  return [
    {
      id: "hoy",
      title: "Hoy",
      cards: [
        {
          to: "/app/renata/chat",
          label: "Conversar",
          description: "Hacé preguntas, pedí análisis o ejecutá tareas",
          icon: MessageSquare,
          search: renataChatSearch() as Record<string, string>,
          featureFlag: "chat" as RenataFeatureId,
        },
        {
          to: "/app/renata/insights",
          label: opps > 0 ? `Oportunidades (${opps})` : "Oportunidades",
          description: "Alertas y oportunidades para tu negocio",
          icon: Lightbulb,
        },
        {
          to: "/app/renata/timeline",
          label: "Actividad",
          description: "Todo lo que pasó en el negocio",
          icon: Activity,
        },
        {
          to: "/app/customer-ai",
          label: "Asistente clientes",
          description: "Conversaciones y atención asistida",
          icon: MessageSquare,
        },
        {
          to: "/app/renata/proveedores",
          label: "Compras inteligentes",
          description: "Compará proveedores y gestioná reposición",
          icon: ShoppingCart,
          featureFlag: "smart_buyer" as RenataFeatureId,
        },
      ],
    },
    {
      id: "trabajo",
      title: "Trabajo",
      cards: [
        {
          to: "/app/renata/tasks",
          label: pending > 0 ? `Acciones (${pending})` : "Acciones",
          description: "Propuestas de Renata que esperan tu aprobación",
          icon: Zap,
        },
        {
          to: "/app/renata/missions",
          label: planes > 0 ? `Planes de trabajo (${planes})` : "Planes de trabajo",
          description: "Seguí el avance de cada plan activo",
          icon: Target,
        },
        {
          to: "/app/renata/goals",
          label: "Objetivos",
          description: "Definí metas y medí el progreso",
          icon: Flag,
        },
        {
          to: "/app/renata/planning",
          label: "Planificación",
          description: "Borradores y planes pendientes de activar",
          icon: Map,
          featureFlag: "planning" as RenataFeatureId,
        },
        {
          to: "/app/renata/playbooks",
          label: "Guías de trabajo",
          description: "Procedimientos que Renata aplica en tu negocio",
          icon: BookOpen,
        },
        {
          to: "/app/renata/automatizaciones",
          label: "Automatizaciones",
          description: "Flujos que Renata ejecuta según reglas definidas",
          icon: Workflow,
        },
        {
          to: "/app/renata/inbox",
          label: "Bandeja de captura",
          description: "Elementos capturados para procesar o delegar",
          icon: Inbox,
          featureFlag: "inbox" as RenataFeatureId,
        },
        {
          to: "/app/renata/push",
          label: "Avisos push",
          description: "Notificaciones y alertas que envía Renata",
          icon: Bell,
        },
      ],
    },
    {
      id: "conocimiento",
      title: "Conocimiento del negocio",
      cards: [
        {
          to: "/app/renata/knowledge",
          label: "Biblioteca",
          description: "Documentos y manuales que Renata consulta",
          icon: BookMarked,
          featureFlag: "knowledge" as RenataFeatureId,
        },
        {
          to: "/app/renata/learning",
          label: "Aprendizajes",
          description: "Conocimiento aprendido de tu operación. Revisá y aprobá",
          icon: Sparkles,
        },
      ],
    },
    {
      id: "equipo",
      title: "Equipo",
      cards: [
        {
          to: "/app/renata/handoff",
          label: "Atención humana",
          description: "Conversaciones derivadas a tu equipo",
          icon: UserCheck,
        },
      ],
    },
    {
      id: "ajustes",
      title: "Ajustes",
      cards: [
        {
          to: "/app/renata/store",
          label: "Extensiones",
          description: "Instalá capacidades adicionales para tu rubro",
          icon: Store,
        },
        {
          to: "/app/renata/config",
          label: "Configuración",
          description: "Ajustá el comportamiento general de Renata",
          icon: Settings,
        },
        {
          to: "/app/renata/usage",
          label: "Consumo IA",
          description: "Uso de IA y detalle de consumo",
          icon: BarChart2,
        },
      ],
    },
  ];
}

function RenataHubPage() {
  const tenantId = useWorkspaceTenantId();
  const [stats, setStats] = useState<QuickStats | null>(null);
  const [loading, setLoading] = useState(true);
  const copilotFlags = useRenataFlags();

  async function load() {
    if (!tenantId) return;
    setLoading(true);
    try {
      const headers = clientRequestHeaders(tenantId);
      const [taskRes, missionRes, insightRes] = await Promise.allSettled([
        fetch("/api/renata/tasks?stats=1", { credentials: "include", headers }),
        fetch("/api/renata/missions?stats=1", { credentials: "include", headers }),
        fetch("/api/renata/insights", { credentials: "include", headers }),
      ]);

      const proposed = taskRes.status === "fulfilled" && taskRes.value.ok
        ? await taskRes.value.json().then((d: Record<string, unknown>) => {
            const byStatus = (d.byStatus as Array<{ status: string; count: number }>) ?? [];
            return byStatus.find((r) => r.status === "proposed")?.count ?? 0;
          })
        : 0;

      const missionActive = missionRes.status === "fulfilled" && missionRes.value.ok
        ? await missionRes.value.json().then((d: { stats?: Record<string, unknown> }) => {
            const byStatus = ((d.stats?.byStatus) as Array<{ status: string; count: number }>) ?? [];
            return byStatus.find((r) => r.status === "active")?.count ?? 0;
          })
        : 0;

      const insightOpen = insightRes.status === "fulfilled" && insightRes.value.ok
        ? await insightRes.value.json().then((d: Record<string, unknown>) => {
            const s = (d.stats as Record<string, unknown>) ?? {};
            return Number(s.open ?? 0);
          })
        : 0;

      setStats({ pendingAprobaciones: proposed, planesActivos: missionActive, oportunidadesAbiertas: insightOpen });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [tenantId]);

  const today = new Date().toLocaleDateString("es-AR", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  const allSections = buildSections(stats);
  const sections = copilotFlags
    ? allSections.map((s) => ({
        ...s,
        cards: s.cards.filter((c) => !c.featureFlag || copilotFlags[c.featureFlag] !== false),
      })).filter((s) => s.cards.length > 0)
    : allSections;
  const pending = stats?.pendingAprobaciones ?? 0;

  return (
    <div className="overflow-y-auto p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <RenataAvatar size="md" />
          <div>
            <h1 className="text-base font-semibold leading-tight">Renata</h1>
            <p className="text-xs text-muted-foreground capitalize">{today}</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="h-8 gap-1.5 text-muted-foreground">
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          <span className="hidden sm:inline text-xs">Actualizar</span>
        </Button>
      </div>

      {/* Pending approvals banner */}
      {!loading && pending > 0 && (
        <Link to="/app/renata/tasks">
          <div className="mb-5 flex items-center justify-between gap-3 rounded-xl border border-violet-200 bg-violet-50/60 px-4 py-3 dark:border-violet-800 dark:bg-violet-950/20">
            <div className="flex items-center gap-2.5">
              <Clock className="h-4 w-4 shrink-0 text-violet-600 dark:text-violet-400" />
              <p className="text-sm font-medium text-violet-900 dark:text-violet-100">
                {pending} propuesta{pending !== 1 ? "s" : ""} esperando tu aprobación
              </p>
            </div>
            <ArrowRight className="h-4 w-4 shrink-0 text-violet-500" />
          </div>
        </Link>
      )}

      {/* Hub sections */}
      <div className="space-y-6">
        {sections.map((section) => (
          <div key={section.id}>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {section.title}
            </p>
            <div className={MERCHANT_HUB_GRID}>
              {section.cards.map((card) => (
                <DashboardCard
                  key={card.to}
                  to={card.to}
                  search={card.search}
                  icon={card.icon}
                  title={card.label}
                  subtitle={card.description}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
